import React from 'react';
import { Link } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import LegalHero from './LegalHero.js';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';

const FLARE_RED = '#c4151c';
const ACCENT = '#4f46e5';

export default function ShopTermsOfServiceSection() {
  const brand = useSiteBrand();
  const email = 'digital@flare.pk';
  const address = brand.officeHqAddress
    || '2nd Floor, Digital Flare Network, Main Boulevard, Gulberg III, Lahore, 54660, Pakistan';

  return (
    <ShopLayout pageTitle="Terms of Service" currentPage="terms">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <LegalHero
          kicker="digital.flare.pk"
          title="Terms of Service"
          updated={<>Last updated: <span className="text-gray-700">October 21, 2026</span></>}
          image="/legal/terms.png"
          accent={ACCENT}
        />

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-7 text-gray-600 leading-relaxed">
          <p>
            By using <strong className="text-gray-900">digital.flare.pk</strong>, you agree to these Terms.
            The site is operated by <strong className="text-gray-900">Digital Flare Network (Private) Limited</strong> (Flare Magazine), Lahore, Pakistan.
          </p>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">1. What we sell</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-gray-900">Printed magazines</strong> and <strong className="text-gray-900">print subscriptions</strong> — prices as shown at checkout.</li>
              <li><strong className="text-gray-900">Digital PDFs</strong> — free where available; not sold separately.</li>
              <li>You must be 18+ to place a paid order. Prices are in PKR and may change; confirmed paid orders are honoured at the checkout price.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">2. Orders &amp; delivery</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>After payment is confirmed, we pack and dispatch printed copies via our courier partners across Pakistan.</li>
              <li>Delivery times vary by city. You are responsible for a correct, complete shipping address and reachable phone number.</li>
              <li>Once an order is placed, customers <strong className="text-gray-900">cannot cancel</strong> it for a voluntary refund. See our{' '}
                <Link to="/refund-policy" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>Refund Policy</Link>.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">3. Subscriptions</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Plan length and issue count are as stated on the <Link to="/plans" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>Plans</Link> page.</li>
              <li>Subscriptions do not auto-renew unless you opt in. Renewal reminders may be sent before the plan ends.</li>
              <li>Pause or delivery questions: email{' '}
                <a href={`mailto:${email}`} className="font-semibold hover:underline" style={{ color: FLARE_RED }}>{email}</a>.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">4. Payments</h2>
            <p>
              We accept JazzCash wallet, card (via JazzCash), and bank transfer as shown at checkout.
              COD is not available for website orders. You must complete payment as instructed for the order to be processed.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">5. Refunds</h2>
            <p>
              Refunds and replacements are governed by our{' '}
              <Link to="/refund-policy" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>Refund Policy</Link>.
              In short: refunds for stock unavailability or fulfilment errors on our side; no customer-initiated cancel refund; free PDFs have no refund.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">6. Content &amp; use</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Magazine content, branding, and site materials are owned by Digital Flare Network (Private) Limited. Personal reading use only.</li>
              <li>Do not redistribute PDFs or print content commercially without written permission.</li>
              <li>Do not misuse the site (fraud, abuse, scraping behind login, or illegal activity).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">7. Liability</h2>
            <p>
              Our liability for any claim related to an order is limited to the amount you paid for that order.
              We are not responsible for delays caused by courier disruption, force majeure, or an incorrect address you provided.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2.5">8. Law &amp; contact</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>These Terms are governed by the laws of Pakistan. Courts in Lahore, Punjab have jurisdiction.</li>
              <li>Privacy practices are described in our <Link to="/privacy-policy" className="font-semibold hover:underline" style={{ color: FLARE_RED }}>Privacy Policy</Link>.</li>
              <li>
                <strong className="text-gray-900">Publisher:</strong> Digital Flare Network (Private) Limited<br />
                <strong className="text-gray-900">Address:</strong> {address}<br />
                <strong className="text-gray-900">Email:</strong>{' '}
                <a href={`mailto:${email}`} className="font-semibold hover:underline" style={{ color: FLARE_RED }}>{email}</a>
                {' · '}
                <strong className="text-gray-900">Phone:</strong> {brand.phone}
              </li>
            </ul>
          </section>
        </div>
      </div>
    </ShopLayout>
  );
}
